import { z } from "zod";
import { MAX_WORK_ORDER_PHOTOS } from "@remi/constants/work-order";
import { TaskAttemptVersion } from "@remi/enums/task-attempt-version";
import { TaskType } from "@remi/enums/task-type";

// v1
const InsuranceApprovalReceivedV1ApprovedDataSchema = z.object({
  attemptType: z.literal("APPROVED"),
  estimateId: z.string(),
  workOrderNotes: z.string().trim().min(1).max(2000).optional(),
  // Optional: existing persisted v1 attempts predate this field and must still parse.
  removedWorkOrderImageIds: z.string().array().max(MAX_WORK_ORDER_PHOTOS).optional(),
});
export type InsuranceApprovalReceivedV1ApprovedData = z.infer<typeof InsuranceApprovalReceivedV1ApprovedDataSchema>;

const InsuranceApprovalReceivedV1RejectedDataSchema = z.object({
  attemptType: z.literal("REJECTED"),
  // No .max(): 3 persisted rows exceed 2000 chars, and this schema is parsed on read.
  rejectionReason: z.string().trim().min(1),
});
export type InsuranceApprovalReceivedV1RejectedData = z.infer<typeof InsuranceApprovalReceivedV1RejectedDataSchema>;

const InsuranceApprovalReceivedV1DataSchema = z.object({
  version: z.literal(TaskAttemptVersion.v1),
  payload: z.discriminatedUnion("attemptType", [
    InsuranceApprovalReceivedV1ApprovedDataSchema,
    InsuranceApprovalReceivedV1RejectedDataSchema,
  ]),
});
export type InsuranceApprovalReceivedV1Data = z.infer<typeof InsuranceApprovalReceivedV1DataSchema>;

// base
export const InsuranceApprovalReceivedDataSchema = z.discriminatedUnion("version", [
  InsuranceApprovalReceivedV1DataSchema,
]);
type InsuranceApprovalReceivedData = z.infer<typeof InsuranceApprovalReceivedDataSchema>;

export const InsuranceApprovalReceivedTaskAttemptSchema = z.object({
  type: z.literal(TaskType.INSURANCE_APPROVAL_RECEIVED),
  data: InsuranceApprovalReceivedDataSchema,
});

export type InsuranceApprovalReceivedTaskAttempt = z.infer<typeof InsuranceApprovalReceivedTaskAttemptSchema>;

type CarrierDecision = { approved: true; workOrderNotes?: string } | { approved: false };

export const getApprovedCarrierDecision = (data: InsuranceApprovalReceivedData): CarrierDecision => {
  if (data.payload.attemptType !== "APPROVED") {
    return { approved: false };
  }
  return { approved: true, workOrderNotes: data.payload.workOrderNotes };
};
